import { SetOptional } from "type-fest";
import find from 'lodash/find';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import entries from 'lodash/entries';
import mapValues from 'lodash/mapValues';
import identify from "~/identify";
import { PreparedSubview, Subview } from "./types/subview.types";
import { PreparedField, FieldOptionsPrepared, PreparedFieldBase, FieldType } from "./types/field.types";
import { hasTarget, iterateGridElements } from '~/data/targetGrid';
import { Model } from "./types/model.types";
import { Grid, Row, RowPrepared, RowWithChildren, TabPrepared } from "./types/grid.types";
import { modify } from "mythil/object";
/**
 * 
 * SUBVIEWS
 * 
 * 
 */
export const prepareSubviews = (subviews) => {
    return keyBy(map(subviews, prepareSubview), 'name') 
};

export const prepareSubview = (subview: Subview) => {
    let name = identify.subview(subview);

    return {
        ...subview,
        group: subview.group ? subview.group : identify.DEFAULT_SUBVIEW_GROUP,
        name,
    };
};


/**
 * 
 * GRID
 * 
 * 
 */


export const prepareGrid = (model: Model, subviews: Record<string, PreparedSubview> | never[] = []) => {
    return mapValues(model.fieldGrid, (grid, name) =>
        prepareGridGroup(model, grid, name === 'default' ? subviews : [])
    );
};

function prepareRowTheme(node) {
    const theme = node.theme
    node = modify(node, "rowTheme", ["light", "dark"].includes(theme) ? theme : undefined);

    delete node.theme;
    return node;
}


const prepareGridGroup = (model: Model, grid: Grid, subviews: Record<string, PreparedSubview> | never[] = []) => {
    let rows = grid.rows,
        defaultLevel = prepareRowTheme({theme: model.defaultLevel}).rowTheme || 'light';

    if (Array.isArray(grid.rows) && (grid.rows).length < 1) {
        rows = convertGroupsToGrid(model.fieldGroups, defaultLevel);
    }

    const shouldCreateSubviewDefaultGroup =
        find(subviews, { group: identify.DEFAULT_SUBVIEW_GROUP }) && !hasTarget(rows, identify.DEFAULT_SUBVIEW_GROUP);

    if (shouldCreateSubviewDefaultGroup) {
        // because we split the screen only have half the amount of elements per group
        if (Array.isArray(grid.rows) && grid.rows.length < 1) {
            rows = convertGroupsToGrid(model.fieldGroups, defaultLevel, { lg: 3, md: 4, sm: 12, xs: 12, toggle: false });
        }

        rows = [
            {
                cols: [
                    { xs: 12, rows: rows },
                    {
                        xs: 12,
                        rows: [
                            {
                                name: identify.DEFAULT_SUBVIEW_GROUP,
                                xs: 12,
                                cols: [],
                                theme: defaultLevel,
                                toggle: false,
                            },
                        ],
                    },
                ],
            },
        ];
    }

    return {
        ...grid,
        rows: prepareRows(rows),
        maxLevel: "light",
    };
};

const convertGroupsToGrid = (groups, level, options = {}) => {
    let defaults = { lg: 3, md: 4, sm: 12, xs: 12, toggle: false };

    options = { ...defaults, ...options };

    return groups.map(({ name, title, open }) => {
        return { name, title, open, level, ...options };
    });
};


export const prepareRows = (rows: Grid['rows'], initialKey = '') => Object.fromEntries(
    Object.entries(rows).map(([key, row]) => {
        // TODO: remove reference for tabs; expect only widget
        const preparedRow = ['widget', "tabs"].includes(row.type)
        ? prepareWidgetRow(row as Row, [initialKey, key].filter(Boolean).join('__'))
        : prepareRow(row, [initialKey, key].filter(Boolean).join('__'));
    
        return [preparedRow.key ?? key, preparedRow];
    })
) as Record<string | number, RowPrepared>;


const prepareWidgetRow = (props: Row, rowKey): RowPrepared => {
    const { nodes, nodeGroups, referenceField, widgetName, displayAs, visible_conditions, ...row } = props as RowWithChildren;

    const deriveNestedRows = (nodes, nodekey, shouldNest) => {
        if (!shouldNest) {
            return prepareRows(nodes as Grid['rows'], nodekey)
        }

        const nestedRowKey = [`${nodekey}__widget`, "nested"].filter(Boolean).join("__");
        return prepareRows([{ cols: [{ rows: nodes }], }], nestedRowKey)
    }

    const prepareNode = (isGroup: boolean) => (node, key): TabPrepared => ({
        ...prepareRowTheme(node),
        ...(isGroup ? {
            key: [rowKey, displayAs, key, isGroup ? "group" : undefined].filter(Boolean).join("__"),
            type: "widget_group",
            nodes: Object.values(mapValues(node.nodes, prepareNode(false))),
            isWidgetGroup: true,
        } : {
            ...node,
            key: (node.value || key) + '',
            icon: node.icon,
            iconFamily: node.icon_family,
            rows: deriveNestedRows(
                node.rows as Grid['rows'],
                [rowKey, displayAs, key, isGroup ? "group" : undefined].filter(Boolean).join("__"),
                ["wizard", "tabs"].includes(displayAs)
            ),
        }),
        uniqueKey: [rowKey, displayAs, key, isGroup ? "group" : undefined].filter(Boolean).join("__"),
        visibleConditions: node.visible_conditions,
        disableConditions: node.disable_conditions,
    });

    const isGroupableWidget = ["big_radio", "big_checkbox"].includes(displayAs);
    return ({
        ...prepareRowTheme(row),
        type: "widget",
        key: rowKey,
        referenceField,
        widgetName: widgetName,
        widgetType: displayAs,
        visibleConditions: visible_conditions,

        // TODO: remove reference for tabs; expect only widget
        nodes: {
            ...keyBy(mapValues(isGroupableWidget ? nodeGroups : [], prepareNode(true)), "uniqueKey"),
            ...keyBy(mapValues(nodes, prepareNode(false)), "uniqueKey"),
        }
    })
}

const prepareRow = (row, rowKey): RowPrepared => ({
    ...prepareRowTheme(row),
    key: rowKey,
    type: row.type || 'row',
    visibleConditions: row.visible_conditions,
    cols: map(row.cols, (col, key) => ({
        ...prepareRowTheme(col), 
        key,
        visibleConditions: col.visible_conditions,
        type: 'col',
        rows: prepareRows(col.rows, rowKey)
    })) ?? []
})


export const prepareGridVisibilityFields = (grid: Record<string, { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }>) => {
    const deriveFields = (grid: { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }): string[] => {
        return Object.values(grid.rows).reduce((fields: string[], row) => {

            fields.push(...(!row.visibleConditions || typeof row.visibleConditions !== "object" ? [] : Object.keys(row.visibleConditions) as string[]))

            if ("cols" in row) {
                const colFields = row.cols.flatMap((col) => Object.values(deriveFields(col))).flat();
                fields.push(...colFields);
            }

            if ("nodes" in row) {
                const nodeFields = Object.values(row.nodes ?? [])
                    .flatMap((tab) => tab.nodes
                        ? map(tab.nodes, (node) => deriveFields(node)).flat()
                        : deriveFields(tab)
                    );

                fields.push(...nodeFields);
            }

            return Array.from(new Set(fields));

        }, !grid.visibleConditions || typeof grid.visibleConditions !== "object" ? [] : Object.keys(grid.visibleConditions) as string[]);
    };

    return mapValues(grid, (row) => deriveFields(row));
}


export const prepareGridControlGroups = (grid: Record<string, { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }>) => {
    const deriveGroups = (grid: { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }) => {
        return Object.values(grid.rows).reduce((prepared: Record<string, string[]>, row) => {
            if ('name' in row) {
                prepared.sources.push(row.name);
                return prepared;
            }

            if ("nodes" in row) {
                const nodeFields = Object.fromEntries(
                    Object.values(row.nodes ?? [])
                        .map((tab) => [
                            tab.key,
                            tab.nodes
                                ? map(tab.nodes, (node) => deriveGroups(node).sources).flat()
                                : deriveGroups(tab).sources
                        ])
                ) as Record<string, string[]>;
                return {
                    ...prepared,
                    ...nodeFields,
                    sources: [...prepared.sources, ...Object.values(nodeFields).flat()]
                }
            }

            if ("cols" in row) {
                return row.cols.reduce((resolved, col) => {
                    const derivedGroups = deriveGroups(col);
                    return {
                        ...resolved, ...derivedGroups, sources: [...resolved.sources, ...derivedGroups.sources]
                    };
                }, prepared);
            }

            return prepared;

        }, { sources: [] });
    };

    return mapValues(grid, (row) => deriveGroups(row));
}




/**
 * 
 * 
 * FIELDS
 * 
 */
export const prepareField = (field: FieldType) => {
    let prepared: PreparedFieldBase = {
        ignored: field.ignored,
        notPersisted: field.notPersisted,
        editable: field.editable,
        visible: field.visible,
        group: field.group,
        type: field.type,
        title: field.title,
        name: field.field_name,
        isAutocomplete: field.autocomplete,
        disableInput: field.disable_input,
        isRange: field.min_max,
        orderable: !!(field.sort_field && field.sort_field.length),
        hasMultipleValues: field.multiple_values,
        order: field.builder_order,
        minQueryLength: field.minimum_input_length === 1 ? 0 : field.minimum_input_length,
        visibleConditions: field.visible_conditions ? field.visible_conditions : [],
        maxOptions: field.num_options,
        tooltip: field.tooltip,
        description: field.description,
        isInline: field.inline,
    };

    if (field.allow_create && field.model) {
        prepared.canCreate = field.allow_create;
        prepared.model = field.model;
    }

    if (field.constraints) {
        prepared.constraints = field.constraints;
    }
    
    if (field.options) {
        prepared.options = prepareOptions(field.options);
    }

    if (field.selections) {
        prepared.selections = prepareSelections(field.selections);
    }

    return prepared;
};


const prepareValueAdapt = (field, prepared: SetOptional<PreparedField, 'adaptValue'>, type) => {
    if (field.adapt_to_filter) {
        prepared = {
            ...prepared,
            adaptValue: {
                fields: [].concat(field.adapt_to_filter),
                mode: identify.VALUE_ADAPT_SUBSTITUTE,
                inputType: identify.FILTERFIELD,
            },
        };

        if (field.adapt_value.eval) {
            prepared.adaptValue.eval = field.adapt_value.eval;
        }
    }

    if (field.adapt_value) {
        prepared = {
            ...prepared,
            adaptValue: {
                fields: [].concat(field.adapt_value.field),
                option: field.adapt_value.option,
                mode: field.adapt_value.mode || identify.VALUE_ADAPT_OVERWRITE,
                inputType: field.adapt_value.inputType || type,
            },
        };

        if (field.adapt_value.eval) {
            prepared.adaptValue.eval = field.adapt_value.eval;
        }
    }

    return prepared  as PreparedField;
};



export const prepareFilter = (filter) => {
    return prepareValueAdapt(filter, {
        ...prepareField(filter),
        inputType: identify.FILTERFIELD as PreparedField['inputType'],
    }, identify.FILTERFIELD);
};




export const prepareEditField = (editField) => {
    let prepared: SetOptional<PreparedField, 'adaptValue'> = {
        ...prepareField(editField),
        inputType: identify.EDITFIELD as PreparedField['inputType'],
        publicPath: editField.public_path,
        isRange: false,
        disableConditions: editField.disable_conditions ?? {},
    };

    prepared = prepareValueAdapt(editField, prepared, identify.EDITFIELD);

    return prepared as PreparedField;
};

export const prepareOptions = (options): FieldOptionsPrepared[] => {
    return options.map((option) => ({
        ...omit(option, ['id']),
        value: isNaN(option.id) ? option.id : parseInt(option.id),
    }));
};

export const prepareSelections = (selections: FieldType['selections']) => entries(selections)
    .map(([key, {icon_family, ...option}]) => ({
        ...option,
        iconFamily: icon_family,
        value: isNaN(+key) ? key : +key,
    })).sort((a, b) => a.builder_order - b.builder_order);
